'use client';

import { c as _c } from "react/compiler-runtime";
import { type DateValue, getLocalTimeZone } from '@internationalized/date';
import { useParams, useSearchParams } from 'next/navigation';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useTripId } from '~/hooks/useTripId';
import { api } from '~/libs/api';
import type { TripOverviewLeg, TripOverviewStay } from '~/services/engine-ts';
import { useTripBookingStore } from '~/stores/tpv2/tripBookingStore';
import { formatTripDateRange } from '~/utils/dateHelpers/dateHelpers';
import { aggregatePax } from '~/utils/paxHelpers/paxHelper';
import { isTripStartInPast } from '../lib/isTripStartInPast';
import { deriveTripEditability } from '../lib/tripEditability';
import { useTripLiveCheck } from './useTripLiveCheck';
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const useTripOverviewSummary = () => {
  const $ = _c(45);
  const tripId = useTripId();
  let t0;
  if ($[0] !== tripId) {
    t0 = {
      tripId
    };
    $[0] = tripId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!tripId;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t1,
      keepPreviousData: true
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: overviewData
  } = api.trip.getOverview.useQuery(t0, t2);
  let t3;
  if ($[4] !== overviewData?.items) {
    t3 = overviewData?.items.find(_temp);
    $[4] = overviewData?.items;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const firstLeg = t3;
  const firstItem = overviewData?.items.find(_temp2);
  const lastItem = overviewData?.items.find(_temp3);
  const originCity = firstLeg?.origin.name ?? "";
  const routeTripId = useParams()?.journeyId;
  const isTemplateDateUnset = useSearchParams()?.get("template") === "1" && routeTripId === tripId;
  let t4;
  if ($[6] !== isTemplateDateUnset || $[7] !== overviewData) {
    t4 = deriveTripEditability(overviewData, isTemplateDateUnset);
    $[6] = isTemplateDateUnset;
    $[7] = overviewData;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    isDateUnset,
    isReadOnly,
    canEditTripSelections
  } = t4;
  const isLiveChecking = useTripLiveCheck(tripId, overviewData?.priceStale === true);
  const confirmedTripId = useTripBookingStore(_temp4);
  let t5;
  if ($[9] !== confirmedTripId || $[10] !== isDateUnset || $[11] !== overviewData?.previousIds || $[12] !== overviewData?.tripDetailsConfirmedAt || $[13] !== tripId) {
    t5 = !isDateUnset && (!!overviewData?.tripDetailsConfirmedAt || confirmedTripId !== null && (confirmedTripId === tripId || (overviewData?.previousIds ?? []).includes(confirmedTripId)));
    $[9] = confirmedTripId;
    $[10] = isDateUnset;
    $[11] = overviewData?.previousIds;
    $[12] = overviewData?.tripDetailsConfirmedAt;
    $[13] = tripId;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const datesConfirmed = t5;
  let t6;
  if ($[15] !== isTemplateDateUnset || $[16] !== overviewData) {
    t6 = isTripStartInPast(overviewData, isTemplateDateUnset);
    $[15] = isTemplateDateUnset;
    $[16] = overviewData;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const isStartDateInPast = t6;
  const dateRange = isDateUnset ? "" : formatTripDateRange(firstItem?.startDate ?? "", lastItem?.endDate ?? "");
  let t7;
  if ($[18] !== overviewData?.pax.travellerMixes) {
    t7 = overviewData?.pax.travellerMixes ?? [];
    $[18] = overviewData?.pax.travellerMixes;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t7) {
    t8 = aggregatePax(t7);
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const {
    adultCount,
    childAges
  } = t8;
  const travellerCount = adultCount + childAges.length;
  const rooms = overviewData?.pax.numberOfRooms ?? 1;
  let t9;
  if ($[22] !== overviewData?.items) {
    t9 = overviewData?.items.filter(_temp5).reduce(_temp6, 0) ?? 0;
    $[22] = overviewData?.items;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const nights = t9;
  let t10;
  if ($[24] !== overviewData) {
    t10 = date => !!overviewData && (overviewData.unavailableStartDatesForPinnedAccommodation.includes(date.toString()) || overviewData.unavailableDaysOfWeek.includes(WEEKDAYS[date.toDate(getLocalTimeZone()).getDay()]));
    $[24] = overviewData;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const isStartDateUnavailable = t10;
  let t11;
  if ($[26] !== adultCount || $[27] !== canEditTripSelections || $[28] !== childAges || $[29] !== dateRange || $[30] !== datesConfirmed || $[31] !== firstItem || $[32] !== isDateUnset || $[33] !== isLiveChecking || $[34] !== isReadOnly || $[35] !== isStartDateInPast || $[36] !== isStartDateUnavailable || $[37] !== lastItem || $[38] !== nights || $[39] !== originCity || $[40] !== overviewData || $[41] !== rooms || $[42] !== travellerCount || $[43] !== tripId) {
    t11 = {
      tripId,
      overviewData,
      firstItem,
      lastItem,
      originCity,
      dateRange,
      travellerCount,
      adultCount,
      childAges,
      rooms,
      nights,
      isReadOnly,
      isDateUnset,
      canEditTripSelections,
      datesConfirmed,
      isStartDateInPast,
      isLiveChecking,
      isStartDateUnavailable
    };
    $[26] = adultCount;
    $[27] = canEditTripSelections;
    $[28] = childAges;
    $[29] = dateRange;
    $[30] = datesConfirmed;
    $[31] = firstItem;
    $[32] = isDateUnset;
    $[33] = isLiveChecking;
    $[34] = isReadOnly;
    $[35] = isStartDateInPast;
    $[36] = isStartDateUnavailable;
    $[37] = lastItem;
    $[38] = nights;
    $[39] = originCity;
    $[40] = overviewData;
    $[41] = rooms;
    $[42] = travellerCount;
    $[43] = tripId;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  return t11;
};
function _temp(i) {
  return i.type === "leg";
}
function _temp2(i_0) {
  return i_0.isFirstItem;
}
function _temp3(i_1) {
  return i_1.isLastItem;
}
function _temp4(s) {
  return s.datesConfirmedTripId;
}
function _temp5(i_2) {
  return i_2.type === "stay";
}
function _temp6(sum, i_3) {
  return sum + (i_3 as TripOverviewStay).nights;
}